import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom-v5-compat";
import {
  hotelsPostBookingApi,
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
} from "@b2bportal/core-hotels";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import {
  LodgingCancelFulfillmentPollResponseEnum,
  type LodgingCancelScenario,
  LodgingCancelScenarioEnum,
  LodgingCancelQuoteResponseEnum,
} from "@b2bportal/lodging-cancel-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { CaseClassValue } from "@hopper-b2b/types";
import { hotelsCancelApi } from "@overrides/utilities";

export type SelfServeHotelCancelPageRouteProps = {
  reservationId: string;
};

export enum SelfServeHotelCancelStepEnum {
  // Generic error state
  error = "error",
  // Initial loading state
  loading = "loading",

  // Cancel scenarios:
  // Should show policy details
  policy = "policy",
  // Needs to be done through customer service
  support = "support",
  // Booking is non-refundable, no action can be taken
  nonrefundable = "nonrefundable",
  // Booking is already cancelled
  cancelled = "cancelled",

  // Polling for cancellation fulfillment
  confirmPolling = "confirmPolling",
  // Cancellation fulfillment was successful
  success = "success",
  // Cancellation fulfillment failed
  tryAgain = "tryAgain",
}

export type SelfServeHotelCancelPageProps = ReturnType<
  typeof useSelfServeHotelCancelPageContainer
> &
  PlatformComponentProps;

export const useValidateSelfServeHotelCancelPageRoute =
  (): SelfServeHotelCancelPageRouteProps => {
    const { reservationId } = useParams<{ reservationId: string }>();
    if (reservationId == null || reservationId === "") {
      throw new Error("Missing or empty reservationId parameter");
    }
    return { reservationId };
  };

export const useSelfServeHotelCancelPageContainer = ({
  reservationId,
}: SelfServeHotelCancelPageRouteProps) => {
  const [cancelScenario, setCancelScenario] =
    useState<LodgingCancelScenario>(null);
  const [activeStep, setActiveStep] = useState<SelfServeHotelCancelStepEnum>(
    SelfServeHotelCancelStepEnum.loading
  );
  const [fulfillId, setFulfillId] = useState<CaseClassValue>(null);

  const [errorMessage, setErrorMessage] = useState<string>(null);

  const { t } = useI18nContext();

  const { data: hotelReservation } =
    hotelsPostBookingApi.useFetchHotelReservationQuery({
      reservationBookingId: reservationId ?? "",
    });

  const fetchCancelPolicy = useCallback(async () => {
    setActiveStep(SelfServeHotelCancelStepEnum.loading);

    try {
      const cancelQuoteResponse =
        await hotelsCancelApi.apiV0SelfserveLodgingCancelQuotePost({
          reservationId,
        });
      if (
        cancelQuoteResponse.data.LodgingCancelQuoteResponse ===
        LodgingCancelQuoteResponseEnum.LodgingCancelQuoteFailure
      ) {
        setActiveStep(SelfServeHotelCancelStepEnum.error);
        setErrorMessage(t("hotels.selfServe.cancel.error.cancelQuote"));
        return;
      }
      const cancelPolicy = cancelQuoteResponse.data;
      const { quoteId, cancelScenario } = cancelPolicy;

      setFulfillId(quoteId);
      setCancelScenario(cancelScenario);

      switch (cancelScenario.LodgingCancelScenario) {
        case LodgingCancelScenarioEnum.FullyRefundable:
        case LodgingCancelScenarioEnum.PartiallyRefundable:
          setActiveStep(SelfServeHotelCancelStepEnum.policy);
          break;

        case LodgingCancelScenarioEnum.NonRefundable:
          setActiveStep(SelfServeHotelCancelStepEnum.nonrefundable);
          break;

        case LodgingCancelScenarioEnum.ContactCustomerService:
        case LodgingCancelScenarioEnum.OutOfPolicy:
        case LodgingCancelScenarioEnum.PastCheckInDate:
        case LodgingCancelScenarioEnum.CfarRefundable:
          setActiveStep(SelfServeHotelCancelStepEnum.support);
          break;

        case LodgingCancelScenarioEnum.Canceled:
          setActiveStep(SelfServeHotelCancelStepEnum.cancelled);
          break;
      }
    } catch {
      setActiveStep(SelfServeHotelCancelStepEnum.error);
      setErrorMessage(t("hotels.selfServe.cancel.error.cancelQuote"));
    }
  }, [setActiveStep, setCancelScenario, setFulfillId, reservationId, t]);

  useEffect(() => {
    if (!cancelScenario) {
      fetchCancelPolicy();
    }
  }, [fetchCancelPolicy, cancelScenario]);

  const emitConfirmCancellationButtonClicked = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelConfirmCancelReservationButtonClicked
  );

  const { refetch: refetchHotelReservations } =
    hotelsPostBookingApi.useFetchAllHotelReservationsQuery();

  const confirmCancel = useCallback(async () => {
    emitConfirmCancellationButtonClicked({
      properties: { cancel_scenario: cancelScenario.LodgingCancelScenario },
    });
    if (fulfillId != null) {
      setActiveStep(SelfServeHotelCancelStepEnum.confirmPolling);
      const quoteId = fulfillId;
      const response =
        await hotelsCancelApi.apiV0SelfserveLodgingCancelFulfillPost({
          quoteId,
        });
      if (
        response.data.LodgingCancelFulfillResponse ===
        "LodgingCancelFulfillFailure"
      ) {
        setActiveStep(SelfServeHotelCancelStepEnum.error);
        setErrorMessage(t("hotels.selfServe.cancel.error.fulfill"));
        return;
      }
      const fulfillmentSessionId = response.data.fulfillmentSessionId;
      let pollComplete = false;

      while (!pollComplete) {
        const res =
          await hotelsCancelApi.apiV0SelfserveLodgingCancelFulfillmentPollPut({
            fulfillmentSessionId,
          });
        switch (res.data.LodgingCancelFulfillmentPollResponse) {
          case LodgingCancelFulfillmentPollResponseEnum.Aborted:
            setActiveStep(SelfServeHotelCancelStepEnum.error);
            setErrorMessage(t("hotels.selfServe.cancel.error.fulfill"));
            refetchHotelReservations();
            pollComplete = true;
            break;
          case LodgingCancelFulfillmentPollResponseEnum.Success:
            setActiveStep(SelfServeHotelCancelStepEnum.success);
            refetchHotelReservations();
            pollComplete = true;
            break;
          case LodgingCancelFulfillmentPollResponseEnum.Pending:
            await new Promise((resolve) => setTimeout(resolve, 2000));
            break;
        }
      }
    }
  }, [
    setActiveStep,
    fulfillId,
    t,
    emitConfirmCancellationButtonClicked,
    cancelScenario?.LodgingCancelScenario,
  ]);

  return useMemo(() => {
    const isReady = cancelScenario != null && hotelReservation != null;
    const isFulfilling =
      activeStep === SelfServeHotelCancelStepEnum.confirmPolling;
    const isLoading = !isReady || isFulfilling;
    const canSelfCancel = activeStep === SelfServeHotelCancelStepEnum.policy;
    return {
      context: {
        activeStep,
        cancelScenario,
        isReady,
        isLoading,
        isFulfilling,
        canSelfCancel,
        hotelReservation,
      },
      handlers: {
        confirmCancel,
      },
      text: {
        title: t("hotels.selfServe.cancel.title"),
        confirmCTA: isFulfilling
          ? t("hotels.selfServe.cancel.confirmCTAProcessing")
          : t("hotels.selfServe.cancel.confirmCTA"),
        errorMessage,
        loading: isFulfilling
          ? t("hotels.selfServe.cancel.processingCancellation")
          : t("hotels.selfServe.cancel.loading"),
      },
    };
  }, [
    activeStep,
    cancelScenario,
    confirmCancel,
    hotelReservation,
    errorMessage,
    t,
  ]);
};
